import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import { useStringsContext } from '../../../../../contexts/strings';
import BaseDialog, { BaseDialogProps } from '../../../../common/base-dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../../firebase-app';
import { setDoc, collection, doc } from 'firebase/firestore';
import { getPollPath } from '../../../../../utils/paths';
import { FS_PATH_POLLS } from '../../../../../constants';
import { Poll, QuizPoll } from '../../types';
import { getStatus } from '../../data';
import PollResultsCard from '../cards/poll-results-card';
import PollNameCard from '../cards/poll-name-time-card';
import PollImagesCard from '../cards/poll-images-card';
import PollTitle from '../poll-title';
import PollValidationPopover from '../poll-validation-popover';
import PollQuizStepper from '../poll-quiz-stepper';
import { SxStyle } from '../../../../../types';
import PollQuizCard from '../cards/poll-quiz-card';
import { useFormValidate } from '../../../../../hooks/formValidate';
import Box from '@mui/material/Box';
import PollDescriptionCard from '../cards/poll-description-card';
import PollQuizRewardCard from '../cards/poll-quiz-reward-card';
import Typography from '@mui/material/Typography';
import { getQuizRewardSummary, getQuizRewardThresholdError } from '../../quiz-reward';

const styles: SxStyle = {
  buttons: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
};

interface Props extends BaseDialogProps {
  onEditDone: () => void;
  onCreateDone: (createdPoll: Poll) => void;
  onDelete: () => void;
  selectedPoll: QuizPoll | null;
  selectedAbbrv: string;
}

const PollQuizModal = ({
  open,
  onClose,
  onEditDone,
  onCreateDone,
  onDelete,
  selectedPoll,
  selectedAbbrv,
}: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  const anchorEl = useRef<any>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideValidationError, setHideValidationError] = useState<boolean>(true);
  const [quizRewardError, setQuizRewardError] = useState<string | null>(null);

  const isEditing = selectedPoll !== null;
  const pollStatus = isEditing ? getStatus(selectedPoll) : null;

  const [activeStep, setActiveStep] = useState(0);

  const pollId = useMemo(() => {
    return selectedPoll ? selectedPoll.id : doc(collection(firebaseApp.db, FS_PATH_POLLS)).id;
  }, [selectedPoll]);

  const { addFormValue, addFormValidation, removeFormValue, isValid, isDirty, editedValues } =
    useFormValidate();

  const onSubmit = useCallback(async () => {
    if (!isDirty || !isValid) {
      setHideValidationError(false);
      setQuizRewardError(null);
      setShowPopover(true);
      return;
    }

    // The reward threshold can only be checked against the questions once they are added
    const questions = editedValues.questions ?? selectedPoll?.questions ?? [];
    const quizReward =
      'quizReward' in editedValues ? editedValues.quizReward : selectedPoll?.quizReward;
    const rewardError = getQuizRewardThresholdError(quizReward, questions.length);
    setQuizRewardError(rewardError);
    if (rewardError) {
      setActiveStep(0);
      setShowPopover(true);
      return;
    }

    setIsSubmitting(true);

    const updatedPoll = isEditing
      ? { ...selectedPoll }
      : ({
          id: pollId,
          abbrv: selectedAbbrv,
          type: 'quiz',
          createdAt: moment().toISOString(),
        } as QuizPoll);

    Object.entries(editedValues).forEach(([key, value]) => {
      updatedPoll[key as keyof QuizPoll] = value as any;
    });

    try {
      const pollPath = getPollPath(pollId);
      await setDoc(doc(firebaseApp.db, pollPath), updatedPoll);
      enqueueSnackbar(isEditing ? strings.polls.editSuccess : strings.messages.saveSuccess, {
        variant: 'success',
      });
      isEditing ? onEditDone() : onCreateDone(updatedPoll);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.error, {
        variant: 'error',
      });
    }

    setIsSubmitting(false);
  }, [
    editedValues,
    enqueueSnackbar,
    isDirty,
    isEditing,
    isValid,
    onCreateDone,
    onEditDone,
    pollId,
    selectedAbbrv,
    selectedPoll,
    strings,
  ]);

  const handleValue = useCallback(
    (key: string, value: any) => {
      addFormValue(key, value);
    },
    [addFormValue]
  );

  const handleValidation = useCallback(
    (key: string, isValid: boolean) => {
      addFormValidation(key, isValid);
    },
    [addFormValidation]
  );

  const handleQuizRewardValue = useCallback(
    (key: string, value: any) => {
      setQuizRewardError(null);
      addFormValue(key, value);
    },
    [addFormValue]
  );

  const handleRemoveValue = useCallback(
    (key: string) => {
      removeFormValue(key);
    },
    [removeFormValue]
  );

  const onNextStep = useCallback(() => {
    if (!isDirty || !isValid) {
      setHideValidationError(false);
      setQuizRewardError(null);
      setShowPopover(true);
      return;
    }
    setHideValidationError(true);
    setActiveStep(1);
  }, [isDirty, isValid]);

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={
          pollStatus !== 'FINISHED'
            ? isEditing
              ? strings.polls.editQuiz
              : strings.polls.newQuiz
            : undefined
        }
        stepper={
          pollStatus !== 'FINISHED' ? <PollQuizStepper activeStep={activeStep} /> : undefined
        }
      >
        <DialogContent dividers ref={anchorEl}>
          <Stack spacing={4}>
            {isEditing && selectedPoll && (
              <PollTitle selectedPoll={selectedPoll} pollStatus={pollStatus} />
            )}
            {isEditing && selectedPoll?.quizReward && (
              <Typography variant="body2">
                {getQuizRewardSummary(selectedPoll.quizReward, selectedPoll.questions?.length ?? 0)}
              </Typography>
            )}
            {isEditing && selectedPoll && pollStatus !== 'UPCOMING' && (
              <PollResultsCard selectedPoll={selectedPoll} />
            )}
            {pollStatus !== 'FINISHED' && (
              <>
                <Box sx={activeStep === 0 ? {} : { display: 'none' }}>
                  <Stack spacing={4}>
                    <PollNameCard
                      title={strings.polls.pollNameQuizTitle}
                      label={strings.polls.pollNameQuizLabel}
                      helperText={strings.polls.pollNameQuizHelperText}
                      titleStartEnd={strings.polls.pollQuizTimeStartTitle}
                      selectedPoll={selectedPoll}
                      isEditing={isEditing}
                      onValue={handleValue}
                      onValidation={handleValidation}
                      hideValidationError={hideValidationError}
                    />
                    <PollDescriptionCard
                      selectedPoll={selectedPoll}
                      title={strings.polls.quizDescriptionTitle}
                      label={strings.polls.quizDescriptionInfo}
                      onValue={handleValue}
                      onValidation={handleValidation}
                      hideValidationError={hideValidationError}
                    />
                    <PollQuizRewardCard
                      selectedPoll={selectedPoll}
                      thresholdError={quizRewardError}
                      onValue={handleQuizRewardValue}
                      onValidation={handleValidation}
                      hideValidationError={hideValidationError}
                    />
                    <PollImagesCard
                      titleBackgroundImage={strings.polls.quizBackgroundImageTitle}
                      pollId={pollId}
                      selectedPoll={selectedPoll}
                      onValue={handleValue}
                      onValidation={handleValidation}
                      hideValidationError={hideValidationError}
                    />
                  </Stack>
                </Box>
                <Box sx={activeStep === 1 ? {} : { display: 'none' }}>
                  <PollQuizCard
                    selectedAbbrv={selectedAbbrv}
                    isEditing={isEditing}
                    pollId={pollId}
                    selectedPoll={selectedPoll}
                    onValue={handleValue}
                    onValidation={activeStep === 1 ? handleValidation : () => {}}
                    onRemoveValue={handleRemoveValue}
                    hideValidationError={hideValidationError}
                  />
                </Box>
                {isEditing && (
                  <Button
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                    color="error"
                    onClick={onDelete}
                  >
                    {strings.polls.deleteQuiz}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {pollStatus === 'FINISHED' ? (
            <Button variant="contained" color="primary" onClick={onClose}>
              {strings.actions.close}
            </Button>
          ) : (
            <Stack sx={styles.buttons}>
              <Button color="primary" onClick={onClose}>
                {strings.actions.close}
              </Button>
              {activeStep === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!isDirty}
                  onClick={onNextStep}
                >
                  {strings.actions.next}
                </Button>
              )}
              {activeStep === 1 && (
                <Stack direction="row" spacing={2}>
                  <Button color="primary" onClick={() => setActiveStep(0)}>
                    {strings.actions.back}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={!isDirty}
                    loading={isSubmitting}
                    onClick={onSubmit}
                  >
                    {isEditing ? strings.actions.save : strings.polls.submitQuiz}
                  </LoadingButton>
                </Stack>
              )}
            </Stack>
          )}
        </DialogActions>
      </BaseDialog>
      <PollValidationPopover
        showPopover={showPopover}
        anchorElRef={anchorEl.current}
        message={quizRewardError ?? undefined}
        onClose={() => setShowPopover(false)}
      />
    </>
  );
};

export default PollQuizModal;
